import { z } from 'zod';

export const LOCALES = ['he', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'he';

export const LocalizedText = z.object({ en: z.string(), he: z.string() });
export type LocalizedText = z.infer<typeof LocalizedText>;

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function dirOf(locale: Locale): 'rtl' | 'ltr' {
  return locale === 'he' ? 'rtl' : 'ltr';
}

/** Picks the text for a locale, falling back to English, then to any non-empty value. */
export function pick(text: LocalizedText | undefined | null, locale: Locale): string {
  if (!text) return '';
  return text[locale] || text.en || text.he || '';
}

export function lt(en: string, he: string): LocalizedText {
  return { en, he };
}

export type Dictionary = Record<string, LocalizedText>;

/**
 * Builds a translate function over a dictionary. Placeholders use `{name}` syntax.
 * Unknown keys return the key itself so missing strings are visible during QA.
 */
export function createTranslator<D extends Dictionary>(dict: D, locale: Locale) {
  return function t(key: keyof D & string, vars?: Record<string, string | number>): string {
    const entry = dict[key];
    let text = entry ? pick(entry, locale) : key;
    if (vars) {
      for (const [name, value] of Object.entries(vars)) {
        text = text.replaceAll(`{${name}}`, String(value));
      }
    }
    return text;
  };
}
